import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

const linkLesson0410 = getLinkForCourseLesson(
  "e0db4477-1995-4380-9c35-2338dcf5e504",
  "2691a170-54a0-44ae-a39f-bbd688e6072e"
);

const theory = ` <h3 class=sectionHead>Introduction</h3>
<p>Electrostatic microphones are widely used, both in sound recording (voice, piano, acoustic instruments, percussion, etc.) and in metrology (as measurement microphones). These microphones use the electrostatic transduction described in <a href="${linkLesson0410}" target="_blank">Lesson 10</a>. The object of this Lesson is to study the behaviour of such an electrostatic microphone. A simplified description is given, based on the use of an equivalent lumped element electrical network. It allows to study the influence of various parameters on the frequency behaviour of the microphone.
<h3 class=sectionHead>The electrostatic microphone under study</h3>
<p>
   The electrostatic microphone under study is represented schematically in <a href=# data-img-alt="Figure 1. The omnidirectional electrostatic microphone under study." data-img-src=${config.ASSETS_URL}/04/16/theory/figure-1.svg>Figure 1</a>. This is a simple-shaped omnidirectional condenser microphone. It consists of two planar electrodes, one fixed and one movable, separated by a thin air gap.<br class=newline>
   <figure class=figure>
<p><img alt="Figure 1. The omnidirectional electrostatic microphone under study." src=${config.ASSETS_URL}/04/16/theory/figure-1.svg><figcaption class=caption>Figure 1. The omnidirectional electrostatic microphone under study.</figcaption></figure>
<p>
   The microphone membrane acts as the moving electrode. It is a circular, metalized, stretched diaphragm with a surface area of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>S</mi>
   </math>
   and thickness
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>h</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>. The mechanical strain applied to the diaphragm is denoted
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ν</mi>
   </math>. Below its first resonance mode, the movement of the membrane is considered as that of a piston. The membrane can then be assimilated to a mechanical harmonic oscillator defined by
<ul class=itemize1>
   <li class=itemize>
      its mass
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi>m</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mi>S</mi>
         <msub>
            <mrow>
               <mi>h</mi>
            </mrow>
            <mrow>
               <mi>m</mi>
            </mrow>
         </msub>
         <msub>
            <mrow>
               <mi>ρ</mi>
            </mrow>
            <mrow>
               <mi>m</mi>
            </mrow>
         </msub>
      </math>, where
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>ρ</mi>
            </mrow>
            <mrow>
               <mi>m</mi>
            </mrow>
         </msub>
      </math>
      is the mass density of the membrane,
   <li class=itemize>
      its mechanical compliance
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>C</mi>
            </mrow>
            <mrow>
               <mi>m</mi>
            </mrow>
         </msub>
      </math>
      which can be estimated, for a circular membrane, by
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>C</mi>
            </mrow>
            <mrow>
               <mi>m</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mn>1</mn>
            </mrow>
            <mrow>
               <mn>8</mn>
               <mi mathvariant=italic>πν</mi>
            </mrow>
         </mfrac>
      </math>
      [5],
   <li class=itemize>
      and its mechanical resistance
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>m</mi>
            </mrow>
         </msub>
      </math>
      which reflects the mechanical losses.
</ul>
<p>
   The backing electrode is a fixed metal plate. It is generally perforated in order to adjust the acoustic resistance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
   </math>
   that dampens the resonance of the diaphragm. The thickness of the air gap trapped between the two electrodes is noted
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>.
<p>
   The housing of the microphone decouples the rear face of the diaphragm from the incident pressure. It includes a rear cavity whose acoustic compliance is noted
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>, where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>
   is the volume of the cavity,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   is the density of the air and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   is the adiabatic speed of sound. The vent in the wall of the rear cavity allows the static pressure between the front and rear sides of the diaphragm to be equalized. As a first approximation, we consider that this vent has no effect on the acoustic response of the microphone.
<p>
   A bias voltage
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   is applied between these two electrodes through a bias resistor
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>. The electrical capacitance at rest is denoted
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>.
<p>
   The membrane is excited on its front side by a harmonic incident acoustic pressure
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>, considered to be uniform over the entire surface of the membrane. The electrical voltage delivered by the microphone across the input impedance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>
   of the preamplifier to which it is connected is denoted
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>u</mi>
   </math>.
<h3 class=sectionHead>Lumped elements equivalent electrical network</h3>
<p>
   The set of dynamic forces acting on the diaphragm of the electrostatic microphone under study is schematically represented in <a href=# data-img-alt="Figure 2. Dynamic forces acting on the diaphragm." data-img-src=${config.ASSETS_URL}/04/16/theory/figure-2.svg>Figure 2</a>. The diaphragm is subject to the pressure forces
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>S</mi>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>S</mi>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   due to the incident pressure
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and the acoustic pressure
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   at the back of the diaphragm, respectively. It is also subject to the electrostatic force
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   which attracts it towards the fixed backing electrode. In addition, when the diaphragm experiences a displacement
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ξ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   at a velocity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>v</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>, it is subject to both a restoring force
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>ξ</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>
   and a frictional force
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mi>v</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   which oppose the displacement. Taking all these forces into account, the vector projection on x-axis of second Newton’s law gives
<table class=equation>
   <tr>
      <td style="font-size: 7px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>S</mi>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>−</mo>
            <mi>S</mi>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>b</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <mi>v</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <mi>ξ</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <mi>a</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>a</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   the acceleration of the membrane. In the harmonic regime, it yields
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>S</mi>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <mi>S</mi>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi>b</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <mi>V</mi>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jω</mi>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>jω</mi>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <mi>V</mi>
         </math>
      <td class=eq-no>(2)
</table>
<figure class=figure>
   <p>
      <img alt="Figure 2. Dynamic forces acting on the diaphragm." src=${config.ASSETS_URL}/04/16/theory/figure-2.svg>
      <figcaption class=caption>Figure 2. Dynamic forces acting on the diaphragm.</figcaption>
</figure>
<p>
   The equation 2 describes the mechanical behaviour of the membrane, which can be represented equivalently by the lumped elements electrical network of <a href=# data-img-alt="Figure 3. Electrical network equivalent to mechanical behaviour of the membrane." data-img-src=${config.ASSETS_URL}/04/16/theory/figure-3.svg>Figure 3</a>. This lumped element network can be enhanced by adding both the electrical and acoustic parts in order to describe the behaviour of the whole microphone. Using the electrical representation equivalent to electrostatic transduction presented in <a href="${linkLesson0410}" target="_blank">Lesson 10</a>, it yields to the complete network given in <a href=# data-img-alt="Figure 4. Lumped electrical network describing the behaviour of the electrostatic microphone." data-img-src=${config.ASSETS_URL}/04/16/theory/figure-4.svg>Figure 4</a>.
   <figure class=figure>
<p><img alt="Figure 3. Electrical network equivalent to mechanical behaviour of the membrane." src=${config.ASSETS_URL}/04/16/theory/figure-3.svg><figcaption class=caption>Figure 3. Electrical network equivalent to mechanical behaviour of the membrane.</figcaption></figure><figure class=figure><p><img alt="Figure 4. Lumped electrical network describing the behaviour of the electrostatic microphone." src=${config.ASSETS_URL}/04/16/theory/figure-4.svg><figcaption class=caption>Figure 4. Lumped electrical network describing the behaviour of the electrostatic microphone.</figcaption></figure>
<p>In this network:
<ul class=itemize1>
   <li class=itemize>
      the acoustic source impedance
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi>s</mi>
            </mrow>
         </msub>
      </math>
      is no more than the radiation impedance of the diaphragm which radiates a proper acoustic field when set in motion by the incident pressure
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>p</mi>
            </mrow>
            <mrow>
               <mi>i</mi>
            </mrow>
         </msub>
      </math>,
   <li class=itemize>
      the impedance
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
            </mrow>
         </msub>
         <mo class=MathClass-bin>+</mo>
         <mi>j</mi>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
            </mrow>
         </msub>
         <mi>ω</mi>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mn>1</mn>
            </mrow>
            <mrow>
               <mi>j</mi>
               <msub>
                  <mrow>
                     <mi>C</mi>
                  </mrow>
                  <mrow>
                     <mi>a</mi>
                  </mrow>
               </msub>
               <mi>ω</mi>
            </mrow>
         </mfrac>
      </math>
      is the acoustic impedance presented at the back of the membrane. The resistive part is mainly due to the resistance
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
            </mrow>
         </msub>
      </math>
      of the holes in the rear electrode, the capacitive part is mainly due to the compliance
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>C</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
            </mrow>
         </msub>
      </math>
      of the rear volume, and the inductive part is mainly due to the displacement of the air in the air gap and in the holes,
   <li class=itemize>
      the impedance
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi>m</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>m</mi>
            </mrow>
         </msub>
         <mo class=MathClass-bin>+</mo>
         <mi>j</mi>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi>m</mi>
            </mrow>
         </msub>
         <mi>ω</mi>
         <mo class=MathClass-bin>+</mo>
         <mfrac>
            <mrow>
               <mn>1</mn>
            </mrow>
            <mrow>
               <mi>j</mi>
               <msub>
                  <mrow>
                     <mi>C</mi>
                  </mrow>
                  <mrow>
                     <mi>m</mi>
                  </mrow>
               </msub>
               <mi>ω</mi>
            </mrow>
         </mfrac>
      </math>
      is the mechanical impedance of the membrane,
   <li class=itemize>
      the factor
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>k</mi>
            </mrow>
            <mrow>
               <mi>b</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <msub>
                  <mrow>
                     <mi>C</mi>
                  </mrow>
                  <mrow>
                     <mn>0</mn>
                  </mrow>
               </msub>
               <msub>
                  <mrow>
                     <mi>U</mi>
                  </mrow>
                  <mrow>
                     <mn>0</mn>
                  </mrow>
               </msub>
            </mrow>
            <mrow>
               <msub>
                  <mrow>
                     <mi>d</mi>
                  </mrow>
                  <mrow>
                     <mn>0</mn>
                  </mrow>
               </msub>
            </mrow>
         </mfrac>
      </math>
      is the electrostatic coupling factor,
   <li class=itemize>
      the impedance
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi>e</mi>
            </mrow>
         </msub>
      </math>
      is the input electrical impedance of the pre-amplifier.
</ul>
<p>
   As a first approximation, the source impedance is neglected <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mo>(</mo><msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
      <mo>)</mo>
   </math> and the input electrical impedance of the preamplifier is considered infinite <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <mo>(</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>∞</mi>
      <mo>)</mo>
   </math>. Under these conditions, the electrical network of <a href=# data-img-alt="Figure 4. Lumped electrical network describing the behaviour of the electrostatic microphone." data-img-src=${config.ASSETS_URL}/04/16/theory/figure-4.svg>Figure 4</a>, once all the elements have been brought back into the mechanical domain, can be put into the form shown in <a href=# data-img-alt="Figure 5. Lumped electrical network in mechanical domain." data-img-src=${config.ASSETS_URL}/04/16/theory/figure-5.svg>Figure 5</a>, where
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msubsup>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msup>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(3)
</table>
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msubsup>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msup>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(4)
</table>
<p>and where
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msubsup>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(5)
</table>
<figure class=figure>
   <p>
      <img alt="Figure 5. Lumped electrical network in mechanical domain." src=${config.ASSETS_URL}/04/16/theory/figure-5.svg>
      <figcaption class=caption>Figure 5. Lumped electrical network in mechanical domain.</figcaption>
</figure>
<h3 class=sectionHead>Microphone sensitivity</h3>
<p>
   From the equivalent electrical network given in <a href=# data-img-alt="Figure 5. Lumped electrical network in mechanical domain." data-img-src=${config.ASSETS_URL}/04/16/theory/figure-5.svg>Figure 5</a>, it is easy to express the force
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
      <mi>U</mi>
   </math>
   as a function of the force
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>S</mi>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
<table class=equation>
   <tr>
      <td style="font-size: 9px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>b</mi>
               </mrow>
            </msub>
            <mi>U</mi>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mi>S</mi>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mfrac>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msubsup>
                           <mrow>
                              <mi>k</mi>
                           </mrow>
                           <mrow>
                              <mi>b</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                     <mrow>
                        <mi>j</mi>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                        <mi>ω</mi>
                     </mrow>
                  </mfrac>
               </mrow>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msubsup>
                           <mrow>
                              <mi>k</mi>
                           </mrow>
                           <mrow>
                              <mi>b</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                     <mrow>
                        <mi>j</mi>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                        <mi>ω</mi>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>−</mo>
                  <mfrac>
                     <mrow>
                        <msubsup>
                           <mrow>
                              <mi>k</mi>
                           </mrow>
                           <mrow>
                              <mi>b</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                     <mrow>
                        <mi>j</mi>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                        <mi>ω</mi>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <msubsup>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msubsup>
                  <mo class=MathClass-bin>+</mo>
                  <mi>j</mi>
                  <msubsup>
                     <mrow>
                        <mi>M</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msubsup>
                  <mi>ω</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mi>j</mi>
                        <msubsup>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                           <mrow>
                              <mi>′</mi>
                           </mrow>
                        </msubsup>
                        <mi>ω</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(6)
</table>
<p>
   Noting that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>d</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>, Eq. (6) leads to the following expression of sensitivity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>μ</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>
   of the microphone
<table class=equation>
   <tr>
      <td style="font-size: 9px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>μ</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <mi>S</mi>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msubsup>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msubsup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mi>j</mi>
                  <msubsup>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msubsup>
                  <msubsup>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msubsup>
                  <mi>ω</mi>
                  <mo class=MathClass-bin>−</mo>
                  <msubsup>
                     <mrow>
                        <mi>M</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msubsup>
                  <msubsup>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msubsup>
                  <msup>
                     <mrow>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   As a first approximation, we can consider on the one hand that the volume of the rear cavity is sufficiently large so that the total mechanical compliance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msubsup>
   </math>
   is very close to the compliance of the diaphragm
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msubsup>
      <mo class=MathClass-rel>≈</mo>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>
   and, on the other hand, that the resistance due to the holes in the backing electrode is sufficiently high compared to the mechanical resistance of the diaphragm, so that the total mechanical resistance can be written as
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msubsup>
      <mo class=MathClass-rel>≈</mo>
      <msup>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
   </math>. Thus, the sensitivity of the microphone can be written as
<table class=equation>
   <tr>
      <td style="font-size: 8px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>μ</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <mi>S</mi>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mi>j</mi>
                  <msup>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
                  <mi>ω</mi>
                  <mo class=MathClass-bin>−</mo>
                  <msubsup>
                     <mrow>
                        <mi>M</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msubsup>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
                  <msup>
                     <mrow>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   Typical shapes of Bode diagrams of the modulus
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mrow>
         <mo form=prefix fence=true>|</mo>
         <mrow>
            <mfrac>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </mrow>
         <mo form=postfix fence=true>|</mo>
      </mrow>
   </math>
   are given in <a href=# data-img-alt="Figure 6. Sensitivity for various <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>C</mi></mrow><mrow><mi>m</mi></mrow></msub></math>." data-img-src=${config.ASSETS_URL}/04/16/theory/figure-6.svg>Figure 6</a> and <a href=# data-img-alt="Figure 7. Sensitivity for various <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>R</mi></mrow><mrow><mi>a</mi></mrow></msub></math>." data-img-src=${config.ASSETS_URL}/04/16/theory/figure-7.svg>Figure 7</a>. The useful frequency range is the one below the resonant frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mn>2</mn>
            <mi>π</mi>
         </mrow>
      </mfrac>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <msqrt>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>M</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msubsup>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
               </mrow>
            </msqrt>
         </mrow>
      </mfrac>
   </math>. Assuming that the influence of the pressure equalisation hole is not taken into account, the useful frequency range extends in low frequencies to the DC. In this useful frequency range, the sensitivity is written
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>μ</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <mi>S</mi>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(9)
</table>
<p>It is proportional to the area of the diaphragm and the bias voltage and inversely proportional to the inter-electrode distance. It is also proportional to the compliance (or inversely proportional to the stiffness) of the diaphragm.
<p>
   <a href=# data-img-alt="Figure 6. Sensitivity for various <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>C</mi></mrow><mrow><mi>m</mi></mrow></msub></math>." data-img-src=${config.ASSETS_URL}/04/16/theory/figure-6.svg>Figure 6</a> illustrates the influence of the membrane compliance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>. This figure shows a classic trade-off between gain and bandwidth. Indeed, increasing the compliance of the membrane allows an increase in the value of the sensitivity across the bandwidth. At the same time however, this leads to a narrowing of the bandwidth because the resonance frequency of the diaphragm decreases as the compliance increases.
   <figure class=figure>
<p><img alt="Figure 6. Sensitivity for various <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>C</mi></mrow><mrow><mi>m</mi></mrow></msub></math>." src=${config.ASSETS_URL}/04/16/theory/figure-6.svg><figcaption class=caption>Figure 6. Sensitivity for various <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>C</mi></mrow><mrow><mi>m</mi></mrow></msub></math>.</figcaption></figure>
<p>
   <a href=# data-img-alt="Figure 7. Sensitivity for various <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>R</mi></mrow><mrow><mi>a</mi></mrow></msub></math>." data-img-src=${config.ASSETS_URL}/04/16/theory/figure-7.svg>Figure 7</a> illustrates the influence of the acoustic resistance on the microphone response. Obviously, there is an optimal value for the resistance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
   </math>
   which allows the widest possible bandwidth to be obtained while avoiding a resonance peak which is too pronounced at the resonance frequency of the membrane. It is generally this optimal value that is sought to be adjusted by perforating the rear electrode.
   <figure class=figure>
<p><img alt="Figure 7. Sensitivity for various <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>R</mi></mrow><mrow><mi>a</mi></mrow></msub></math>." src=${config.ASSETS_URL}/04/16/theory/figure-7.svg><figcaption class=caption>Figure 7. Sensitivity for various <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>R</mi></mrow><mrow><mi>a</mi></mrow></msub></math>.</figcaption></figure>
<h3 class=sectionHead>More realistic modelling</h3>
<p>
   The modelling of an electrostatic microphone presented in the previous paragraphs is based on simplifying approximations. But more realistic models can be developed.<br class=newline>Firstly, the effect of the static pressure equalisation vent can be taken into account. The presence of this vent leads to a drop in sensitivity at very low frequencies as the acoustic pressure tends to be the same on each side of the diaphragm. At these frequencies, the vent is a capillary tube whose acoustic impedance is a pure acoustic resistance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>av</mi>
         </mrow>
      </msub>
   </math>. The combination of this resistance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>av</mi>
         </mrow>
      </msub>
   </math>
   and the acoustic compliance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
   </math>
   of the rear volume of the microphone creates a high-pass acoustic filter with a cut-off angular frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>av</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>. The sensitivity of the microphone can then be written
<table class=equation>
   <tr>
      <td style="font-size: 7px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>μ</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <mi>S</mi>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mi>j</mi>
                  <mfrac>
                     <mrow>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mi>c</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mi>j</mi>
                  <mfrac>
                     <mrow>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mi>c</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mi>j</mi>
                  <msup>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
                  <mi>ω</mi>
                  <mo class=MathClass-bin>−</mo>
                  <msubsup>
                     <mrow>
                        <mi>M</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msubsup>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
                  <msup>
                     <mrow>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(10)
</table>
<p>
   and the frequency variation of the modulus of this sensitivity is then represented in <a href=# data-img-alt="Figure 8. Effect of the static pressure equalisation vent." data-img-src=${config.ASSETS_URL}/04/16/theory/figure-8.svg>Figure 8</a> where the effect of the pressure equalization vent is visible at low frequencies.
   <figure class=figure>
<p><img alt="Figure 8. Effect of the static pressure equalisation vent." src=${config.ASSETS_URL}/04/16/theory/figure-8.svg><figcaption class=caption>Figure 8. Effect of the static pressure equalisation vent.</figcaption></figure>
<p>Furthermore, more precise models of the acoustic system behind the diaphragm are available in the literature. For example, the acoustic behaviour of the air gap is described in Refs. [4] or [1], or the influence of the holes in the rear electrode is described in Ref. [2].
<p>Finally, several aspects can be taken into account in order to refine the model. For example and non-exhaustively, the presence of an electrical parasitic capacitance [3] or the fact that the diaphragm does not move as a piston [1] can be addressed.
    
 <h3 class=likesectionHead>Electret microphone</h3>   
<p>Electret microphones are also electrostatic microphones. This kind of microphones aims to use a prepolarized diaphraghm which avoids the requisite of providing an external bias voltage, which is one of the main limitations and criticism to the condenser microphones. This can be possible thanks to specific materials, mainly synthetic polymers, called electrect that are electrically charged during the manufacturing process with a given unit charge density and are able to keep this charge permanently (or quasi-permanently).
<p>The sensitivity <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>μ</mi>
         </mrow>
      </msub>
   </math> 
of an electret microphone, in the usefull frequency range, is given by
    
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>μ</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                   <msub>
                     <mrow>
                        <mi>h</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>σ</mi>
                     </mrow>
                     <mrow>
                        <mn>e</mn>
                     </mrow>
                  </msub>
                  <mi>S</mi>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                   <msub>
                    
                     <mrow>
                        <mo class=MathClass-open>(</mo>
                        <mi>h</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                <mo class=MathClass-bin>+</mo>
                <msub>
                     <mrow>
                        <mi>ε</mi>
                     </mrow>
                     <mrow>
                        <mn>E</mn>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                    <mo class=MathClass-close>)</mo>
                <msub>
                     <mrow>
                        <mi>ε</mi>
                     </mrow>
                     <mrow>
                        <mn>A</mn>
                     </mrow>
                  </msub>
                </mrow>       
            </mfrac>
         </math>
      <td class=eq-no>(11)
</table>
    

 <p>where 
     <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
        <msub>
            <mrow>
                <mi>h</mi>
            </mrow>
            <mrow>
                <mn>1</mn>
            </mrow>
        </msub>
   </math> 
is the thickness of the electret film, 
    <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
        <msub>
            <mrow>
                <mi>ε</mi>
            </mrow>
            <mrow>
                <mn>0</mn>
            </mrow>
        </msub>
   </math> 
is the distance between the electret and the back electrode, 
    <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
        <msub>
            <mrow>
                <mi>ε</mi>
            </mrow>
            <mrow>
                <mn>E</mn>
            </mrow>
        </msub>
   </math>  
is the dielectric constant of the electret, 
    <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
        <msub>
            <mrow>
                <mi>ε</mi>
            </mrow>
            <mrow>
                <mn>A</mn>
            </mrow>
        </msub>
   </math>
is the dielectric constant of air, 
    <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
        <msub>
            <mrow>
                <mi>σ</mi>
            </mrow>
            <mrow>
                <mn>e</mn>
            </mrow>
        </msub>
   </math>  
is the surface charge density, 
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
     <msub>
        <mrow>
            <mi>C</mi>
        </mrow>
        <mrow>
            <mi>m</mi>
        </mrow>
    </msub>
</math>  
the mechanical compliance of the diaphragm (or the suspension) and 
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
        <mrow>
            <mi>S</mi>
        </mrow>
</math> 
the effective surface of the diaphragm. 

Note that the sensitivity is here a function of the charge density instead of a bias voltage. Research is continuously intending to develop advance materials able to increase the capability of the electret to admit higher surface charge densities.    
    
<h3 class=likesectionHead>Glossary</h3>
<p>
<dl>
  <dt>DC
  <dd>Direct Current
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt id=X0-bruneau1994equivalent class=thebibliography>[1]
   <dd id=bib-1 class=thebibliography>
      <p>Michel Bruneau et al. “An equivalent network modelling the strong coupling between a vibrating membrane and a fluid film”. In: <span class=cmti-10>Acta acustica</span> 2.3 (1994), pp. 223–232.
   <dt id=X0-lavergne2010dynamic class=thebibliography>[2]
   <dd id=bib-2 class=thebibliography>
      <p>Thomas Lavergne et al. “Dynamic behavior of the circular membrane of an electrostatic microphone: Effect of holes in the backing electrode”. In: <span class=cmti-10>The </span><span class=cmti-10>Journal of the Acoustical Society of America </span>128.6 (2010), pp. 3459–3477.
   <dt id=X0-Rossi1988 class=thebibliography>[3]
   <dd id=bib-3 class=thebibliography>
      <p>Mario Rossi. <span class=cmti-10>Acoustics and Electroacoustics</span>. Artech House, 1988. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 978-0890062555.
   <dt id=X0-vskvor1967acoustical class=thebibliography>[4]
   <dd id=bib-4 class=thebibliography>
      <p>Z Škvor. “On the acoustical resistance due to viscous losses in the air gap of electrostatic transducers”. In: <span class=cmti-10>Acta acustica united with acustica </span>19.5 (1967), pp. 295–299.
   <dt id=X0-skvor1991vibrating class=thebibliography>[5]
   <dd id=bib-5 class=thebibliography>
      <p>Zdenĕk Škvor. “Vibrating systems and their equivalent circuits”. In: <span class=cmti-10>Studies in electrical and electronic engineering </span>40 (1991), pp. 11–241.
</dl>`;

export default theory;
